/**
 * 控制台安全工具 - 防止敏感信息泄露
 * 通过拦截和屏蔽控制台日志中的敏感信息
 * 
 * 版本: 1.1.0
 * 更新内容: 
 * - 添加了错误处理，解决"Unchecked runtime.lastError: The message port closed before a response was received."问题
 * - 优化了对Chrome扩展相关错误的处理
 */

// 敏感数据关键字列表
const sensitiveKeys = [
  'franchiseFee', 'brandUsageFee', 'designFee', 'storeServiceFee', 
  'equipmentCost', 'decorationCost', 'initialCosts', 'total', 'costPerSquareMeter'
];

const ignoredErrorMessages = [
  'The message port closed before a response was received',
  'Extension context invalidated'
];

export const maskSensitiveData = (data: any): any => {
  if (!data) return data;
  
  if (typeof data === 'object' && !Array.isArray(data) && data !== null) {
    const result: any = {};
    for (const key in data) {
      if (sensitiveKeys.includes(key) && typeof data[key] === 'number') {
        result[key] = '*****';
      } else if (key === 'initialCostsData' || key === 'initialCosts') {
        if (data[key]) {
          const maskedData = { ...data[key] };
          
          for (const sensitiveKey of sensitiveKeys) {
            if (maskedData[sensitiveKey] !== undefined && typeof maskedData[sensitiveKey] === 'number') {
              maskedData[sensitiveKey] = '*****';
            }
          }
          
          if (maskedData.decorationCostRules) {
            maskedData.decorationCostRules = maskedData.decorationCostRules.map((rule: any) => ({
              ...rule,
              costPerSquareMeter: '*****'
            }));
          }
          
          result[key] = maskedData;
        } else {
          result[key] = data[key];
        }
      } else if (typeof data[key] === 'object' && data[key] !== null) {
        result[key] = maskSensitiveData(data[key]);
      } else {
        result[key] = data[key];
      }
    }
    return result;
  }
  
  if (Array.isArray(data)) {
    return data.map(item => maskSensitiveData(item));
  }
  
  return data;
};

// 检查是否为应该忽略的错误信息
const shouldIgnoreError = (arg: any): boolean => {
  if (typeof arg === 'string') {
    return ignoredErrorMessages.some(msg => arg.includes(msg));
  }
  
  if (arg instanceof Error) {
    return ignoredErrorMessages.some(msg => arg.message.includes(msg));
  }
  
  if (typeof arg === 'object' && arg !== null) {
    try {
      const strArg = JSON.stringify(arg);
      return ignoredErrorMessages.some(msg => strArg.includes(msg));
    } catch (e) {
      // 序列化失败，无法检查
      return false;
    }
  }
  
  return false;
};

// 安全化控制台日志
export const secureConsole = () => {
  // 保存原始控制台方法
  const originalConsoleLog = console.log;
  const originalConsoleInfo = console.info;
  const originalConsoleDebug = console.debug;
  const originalConsoleWarn = console.warn;
  const originalConsoleError = console.error;

  // 重写控制台日志方法
  console.log = function(...args: any[]) {
    // 屏蔽包含敏感信息的日志参数
    const secureArgs = args.map(arg => {
      // 检查参数是否可能包含敏感信息
      if (typeof arg === 'object' && arg !== null) {
        // 尝试序列化对象以便检查是否包含敏感关键字
        try {
          const strArg = JSON.stringify(arg);
          
          // 检查是否包含敏感关键字
          const containsSensitive = sensitiveKeys.some(key => 
            strArg.includes(`"${key}"`) || 
            strArg.includes(`${key}:`) || 
            strArg.includes(`"${key}":`)
          );
          
          if (containsSensitive) {
            return maskSensitiveData(arg);
          }
        } catch (e) {
          // 序列化失败，无法检查，返回原值
        }
      } else if (typeof arg === 'string') {
        // 检查字符串是否包含敏感关键字
        const containsSensitive = sensitiveKeys.some(key => 
          arg.includes(`"${key}"`) || 
          arg.includes(`${key}:`) || 
          arg.includes(`"${key}":`)
        );
        
        if (containsSensitive) {
          return '*** 包含敏感信息 ***';
        }
      }
      
      return arg;
    });
    
    // 调用原始控制台方法
    originalConsoleLog.apply(console, secureArgs);
  };
  
  // 同样重写其他控制台方法
  console.info = function(...args: any[]) {
    const secureArgs = args.map(arg => {
      if (typeof arg === 'object' && arg !== null) {
        return maskSensitiveData(arg);
      }
      return arg;
    });
    originalConsoleInfo.apply(console, secureArgs);
  };
  
  console.debug = function(...args: any[]) {
    const secureArgs = args.map(arg => {
      if (typeof arg === 'object' && arg !== null) {
        return maskSensitiveData(arg);
      }
      return arg;
    });
    originalConsoleDebug.apply(console, secureArgs);
  };
  
  console.warn = function(...args: any[]) {
    // 检查是否为应该忽略的错误
    if (args.length > 0 && shouldIgnoreError(args[0])) {
      return; // 忽略这些特定的警告
    }
    
    const secureArgs = args.map(arg => {
      if (typeof arg === 'object' && arg !== null) {
        return maskSensitiveData(arg);
      }
      return arg;
    });
    originalConsoleWarn.apply(console, secureArgs);
  };
  
  console.error = function(...args: any[]) {
    // 检查是否为应该忽略的错误
    if (args.length > 0 && shouldIgnoreError(args[0])) {
      return; // 忽略这些特定的错误
    }
    
    const secureArgs = args.map(arg => {
      if (typeof arg === 'object' && arg !== null) {
        return maskSensitiveData(arg);
      }
      return arg;
    });
    originalConsoleError.apply(console, secureArgs);
  };
  
  // 添加全局错误处理以捕获未处理的 runtime.lastError
  if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
      if (event.error && shouldIgnoreError(event.error)) {
        event.preventDefault();
        return false;
      }
      return true;
    });
    
    // 处理未捕获的承诺拒绝
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && shouldIgnoreError(event.reason)) {
        event.preventDefault();
        return false;
      }
      return true;
    });
  }
}; 